import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type Board } from '@air/api/types';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { THUMBNAIL_PREFERENCES } from '~/constants/localStorageKeys';
import { currentThumbnailPreferenceSelector } from '~/store/configViews/selectors';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type ThumbnailPreference = 'contain' | 'default';

type ThumbnailPreferenceStorage = {
  id: Board['id'];
  value: ThumbnailPreference;
};

const THUMBNAIL_PREFERENCES_CLASS_NAME_MAP: { [key in ThumbnailPreference]: string } = {
  default: 'object-cover',
  contain: 'object-contain',
};

export const getPreferenceClassName = (preference?: ThumbnailPreference) => {
  if (preference) {
    return THUMBNAIL_PREFERENCES_CLASS_NAME_MAP[preference];
  }

  return '';
};

export const getPreferenceImgixFit = (preference?: ThumbnailPreference) => (preference === 'default' ? 'crop' : 'clip');

export type ThumbnailPreferenceContextValue = {
  getPreference: (boardId?: string) => ThumbnailPreference | undefined;
  updatePreference: (boardId: string, value: ThumbnailPreference) => void;
};

export const ThumbnailPreferenceContext = createContext<ThumbnailPreferenceContextValue | null>(null);

export type ThumbnailPreferenceContextProviderProps = {
  children: ReactNode;
};

export const ThumbnailPreferenceContextProvider = ({ children }: ThumbnailPreferenceContextProviderProps) => {
  const [preferences, setPreferences] = useState<ThumbnailPreferenceStorage[]>([]);
  const configViewThumbnailPreference = useSelector(currentThumbnailPreferenceSelector);

  useEffect(() => {
    const update = () => {
      const preferences = localStorage.getItem(THUMBNAIL_PREFERENCES);

      if (preferences) {
        try {
          const parsedPreferences = JSON.parse(preferences);
          const updatedPreferences = parsedPreferences.map((preference: ThumbnailPreferenceStorage) => {
            // @ts-expect-error `fill` is not a supported option, we need to convert it to `default` if present
            if (preference.value === 'fill') {
              return { ...preference, value: 'default' };
            }

            return preference;
          });
          setPreferences(updatedPreferences);
        } catch (error) {
          if (isDevOrTestStage()) {
            console.error('Error parsing thumbnail preferences', error);
          } else {
            reportErrorToBugsnag({
              error: convertUnknownToError(error),
              context: 'Error parsing thumbnail preferences',
            });
          }
        }
      }
    };

    window.addEventListener('storage', update);

    update();

    return () => {
      window.removeEventListener('storage', update);
    };
  }, []);

  const updatePreference = useCallback<ThumbnailPreferenceContextValue['updatePreference']>(
    (boardId, value) => {
      let newPreferences: ThumbnailPreferenceStorage[];

      if (preferences.find((preference) => preference.id === boardId)) {
        newPreferences = preferences.map((preference) => {
          if (preference.id === boardId) {
            return { ...preference, value };
          }

          return preference;
        });
      } else {
        newPreferences = [...preferences, { id: boardId, value }];
      }

      setPreferences(newPreferences);

      localStorage.setItem(THUMBNAIL_PREFERENCES, JSON.stringify(newPreferences));
    },
    [preferences],
  );

  const getPreference = useCallback<ThumbnailPreferenceContextValue['getPreference']>(
    (boardId) => {
      const getValue = () => {
        if (configViewThumbnailPreference) {
          return configViewThumbnailPreference;
        }

        if (!boardId) {
          return undefined;
        }

        return preferences?.find((preference) => preference.id === boardId)?.value || 'default';
      };

      const value = getValue();

      // @ts-expect-error `fill` is not a supported option, we need to convert it to `default` if present
      return value === 'fill' ? 'default' : value;
    },
    [preferences, configViewThumbnailPreference],
  );

  const value = useMemo<ThumbnailPreferenceContextValue>(
    () => ({
      getPreference,
      updatePreference,
    }),
    [getPreference, updatePreference],
  );

  return <ThumbnailPreferenceContext.Provider value={value}>{children}</ThumbnailPreferenceContext.Provider>;
};

export const useThumbnailPreference = () => {
  const context = useContext(ThumbnailPreferenceContext);

  if (!context) {
    throw new Error('useThumbnailPreference must be used within ThumbnailPreferenceContextProvider');
  }

  return context;
};
